import { Component, OnInit, inject, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ApiService, showToast } from './api.service';

@Component({
  selector: 'app-admin-purchase-order-detail',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  template: `
    <div class="min-h-full overflow-y-auto admin-premium-bg">
      <div class="max-w-[1400px] mx-auto p-5 space-y-5">
        <header class="flex items-start justify-between gap-4">
          <div>
            <a routerLink="/admin/financials/purchase-orders" class="inline-flex items-center gap-1.5 text-slate-500 hover:text-[#1A6FA6] font-semibold text-xs mb-1">
              <i class="ti ti-arrow-left"></i>Purchase Orders
            </a>
            <h1 class="text-xl font-semibold text-slate-950 tracking-tight">{{ form.po_no || (isExistingOrderRoute ? 'Loading purchase order' : 'New Purchase Order') }}</h1>
            <p class="text-slate-500 text-xs mt-1">{{ isExistingOrderRoute && isLoadingOrder() ? 'Please wait while the order details load.' : 'Build the supplier order, send the PDF, then receive linked materials into stock.' }}</p>
          </div>
          <div class="flex items-center gap-2 flex-wrap justify-end">
            @if (form.id && !isLoadingOrder()) {
              <button type="button" (click)="openPdf()" class="h-9 px-3 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-semibold hover:bg-slate-50 flex items-center gap-2"><i class="ti ti-file-type-pdf"></i>Preview PDF</button>
              <button type="button" (click)="sendToSupplier()" class="h-9 px-3 rounded-lg border border-sky-200 bg-sky-50 text-sky-700 text-xs font-semibold hover:bg-sky-100 flex items-center gap-2"><i class="ti ti-send"></i>Email Supplier</button>
              <button type="button" (click)="updateStock()" [disabled]="!!form.stock_updated_at" class="h-9 px-3 rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 text-xs font-semibold hover:bg-emerald-100 disabled:opacity-50 flex items-center gap-2"><i class="ti ti-package-import"></i>Update Stock</button>
            }
            <button type="button" (click)="save()" [disabled]="isSaving() || isLoadingOrder()" class="h-9 px-4 rounded-lg bg-[#0C3A5A] text-white text-xs font-bold hover:bg-[#082d46] disabled:opacity-50 flex items-center gap-2">
              <i class="ti" [class]="isSaving() ? 'ti-loader animate-spin' : 'ti-device-floppy'"></i>Save
            </button>
          </div>
        </header>

        @if (isLoadingOrder()) {
          <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm p-8 text-center">
            <div class="mx-auto w-11 h-11 rounded-xl bg-sky-50 text-sky-700 flex items-center justify-center">
              <i class="ti ti-loader animate-spin text-2xl"></i>
            </div>
            <h2 class="mt-3 text-base font-bold text-slate-900">Loading purchase order</h2>
            <p class="mt-1 text-sm text-slate-500">The existing order is being loaded. This can take a moment on a weak connection.</p>
          </section>
        } @else {
        <div class="grid grid-cols-1 xl:grid-cols-[1fr_320px] gap-5">
          <div class="space-y-5">
            <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm p-4 space-y-4">
              <div class="flex items-center justify-between border-b border-slate-100 pb-3">
                <div>
                  <p class="text-sm font-bold text-slate-900">Order Details</p>
                  <p class="text-[11px] text-slate-500">Supplier, dates, GST, and audit link.</p>
                </div>
                <span class="px-2.5 py-1 rounded-md text-[10px] font-semibold uppercase border" [ngClass]="statusBadgeClass(form.status)">{{ form.status }}</span>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
                <label class="text-xs font-semibold text-slate-600">Order date <span class="text-rose-500">*</span>
                  <input [(ngModel)]="form.order_date" type="date" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
                </label>
                <label class="text-xs font-semibold text-slate-600">Required date
                  <input [(ngModel)]="form.required_date" type="date" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
                </label>
                <label class="text-xs font-semibold text-slate-600">Status <span class="text-rose-500">*</span>
                  <select [(ngModel)]="form.status" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs bg-white">
                    @for (status of statuses; track status) { <option [value]="status">{{ status }}</option> }
                  </select>
                </label>
                <label class="text-xs font-semibold text-slate-600">GST mode <span class="text-rose-500">*</span>
                  <select [(ngModel)]="form.gst_mode" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs bg-white">
                    <option value="GST">Add GST</option>
                    <option value="No GST">Simple totals</option>
                  </select>
                </label>
              </div>
              <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                <label class="text-xs font-semibold text-slate-600">Supplier <span class="text-rose-500">*</span>
                  <select [(ngModel)]="form.supplier_id" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs bg-white">
                    <option [ngValue]="null">Create new supplier</option>
                    @for (supplier of suppliers(); track supplier.id) { <option [ngValue]="supplier.id">{{ supplier.name }}</option> }
                  </select>
                </label>
                <label class="text-xs font-semibold text-slate-600">Link type <span class="text-rose-500">*</span>
                  <select [(ngModel)]="form.model_type" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs bg-white">
                    <option value="General">General</option>
                    <option value="Job">Job</option>
                    <option value="Project">Project</option>
                  </select>
                </label>
                <label class="text-xs font-semibold text-slate-600">Job / Project ID
                  <input [(ngModel)]="form.reference_id" [disabled]="form.model_type === 'General'" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs disabled:bg-slate-50">
                </label>
              </div>
              @if (!form.supplier_id) {
                <div class="rounded-xl border border-slate-200 bg-slate-50 p-3 grid grid-cols-1 md:grid-cols-3 gap-3">
                  <input [(ngModel)]="form.supplier.name" placeholder="Supplier name *" class="h-9 rounded-lg border border-slate-200 px-3 text-xs">
                  <input [(ngModel)]="form.supplier.email" placeholder="Email" class="h-9 rounded-lg border border-slate-200 px-3 text-xs">
                  <input [(ngModel)]="form.supplier.phone" placeholder="Phone" class="h-9 rounded-lg border border-slate-200 px-3 text-xs">
                  <input [(ngModel)]="form.supplier.contact_person" placeholder="Contact person" class="h-9 rounded-lg border border-slate-200 px-3 text-xs">
                  <input [(ngModel)]="form.supplier.gst_number" placeholder="GST number" class="h-9 rounded-lg border border-slate-200 px-3 text-xs">
                  <input [(ngModel)]="form.supplier.account_number" placeholder="Account number" class="h-9 rounded-lg border border-slate-200 px-3 text-xs">
                  <textarea [(ngModel)]="form.supplier.address" placeholder="Address" rows="2" class="md:col-span-3 rounded-lg border border-slate-200 px-3 py-2 text-xs"></textarea>
                </div>
              } @else if (selectedSupplier(); as supplier) {
                <div class="rounded-xl border border-slate-200 bg-slate-50 p-3">
                  <div class="flex items-center justify-between gap-3 mb-3">
                    <div>
                      <p class="text-xs font-bold text-slate-900">Selected supplier details</p>
                      <p class="text-[11px] text-slate-500">Locked here so the purchase order uses the saved supplier record.</p>
                    </div>
                    <button type="button" (click)="openSupplierModal(supplier)" class="h-8 px-3 rounded-lg border border-sky-200 bg-sky-50 text-sky-700 text-xs font-semibold hover:bg-sky-100 flex items-center gap-1.5">
                      <i class="ti ti-edit"></i>Edit Details
                    </button>
                  </div>
                  <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input [ngModel]="supplier.name" readonly placeholder="Supplier name" class="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-700">
                    <input [ngModel]="supplier.email" readonly placeholder="Email" class="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs text-slate-600">
                    <input [ngModel]="supplier.phone" readonly placeholder="Phone" class="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs text-slate-600">
                    <input [ngModel]="supplier.contact_person" readonly placeholder="Contact person" class="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs text-slate-600">
                    <input [ngModel]="supplier.gst_number" readonly placeholder="GST number" class="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs text-slate-600">
                    <input [ngModel]="supplier.account_number" readonly placeholder="Account number" class="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs text-slate-600">
                    <textarea [ngModel]="supplier.address" readonly placeholder="Address" rows="2" class="md:col-span-3 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs text-slate-600"></textarea>
                  </div>
                </div>
              }
            </section>

            <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm overflow-hidden">
              <div class="px-4 py-3 border-b border-slate-100 flex items-center gap-2 flex-wrap">
                <div class="mr-auto">
                  <p class="text-sm font-bold text-slate-900">Items</p>
                  <p class="text-[11px] text-slate-500">Add materials from stock or manual purchase rows.</p>
                </div>
                <select [(ngModel)]="selectedTemplateId" class="h-9 rounded-lg border border-slate-200 bg-white px-3 text-xs font-semibold text-slate-600">
                  <option [ngValue]="null">Apply template</option>
                  @for (template of templates(); track template.id) { <option [ngValue]="template.id">{{ template.name }}</option> }
                </select>
                <button type="button" (click)="applyTemplate()" [disabled]="!selectedTemplateId" class="h-9 px-3 rounded-lg border border-sky-200 bg-sky-50 text-sky-700 text-xs font-semibold disabled:opacity-50">Apply</button>
                <button type="button" (click)="addManualItem()" class="h-9 px-3 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-semibold hover:bg-slate-50 flex items-center gap-2"><i class="ti ti-plus"></i>Manual Row</button>
              </div>
              <div class="px-4 py-3 border-b border-slate-100">
                <div class="relative max-w-md">
                  <i class="ti ti-search absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"></i>
                  <input [(ngModel)]="materialSearch" (input)="searchMaterials()" placeholder="Search materials to add" class="w-full h-9 pl-9 pr-3 rounded-lg text-xs border border-slate-200 bg-slate-50 focus:bg-white focus:outline-none focus:border-[#1A6FA6]">
                </div>
                @if (materialResults().length > 0) {
                  <div class="mt-2 flex gap-2 overflow-x-auto">
                    @for (mat of materialResults(); track mat.id) {
                      <button type="button" (click)="addMaterialItem(mat)" class="shrink-0 px-3 py-2 rounded-lg border border-sky-100 bg-sky-50 text-left hover:bg-sky-100">
                        <p class="text-xs font-semibold text-sky-900">{{ mat.name }}</p>
                        <p class="text-[10px] text-sky-700">{{ mat.serial_no }} · Stock {{ mat.stock_quantity || 0 }}</p>
                      </button>
                    }
                  </div>
                }
              </div>
              <div class="overflow-x-auto">
                <table class="w-full text-left">
                  <thead><tr class="bg-slate-50/80 border-b border-slate-100">
                    <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest min-w-[260px]">Item <span class="text-rose-500">*</span></th>
                    <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest w-24">Qty <span class="text-rose-500">*</span></th>
                    <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest w-24">Unit</th>
                    <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest w-32">Rate</th>
                    <th class="px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-widest text-right w-32">Total</th>
                    <th class="w-12"></th>
                  </tr></thead>
                  <tbody class="divide-y divide-slate-100">
                    @for (item of form.items; track $index) {
                      <tr>
                        <td class="px-4 py-3">
                          <input [(ngModel)]="item.title" placeholder="Item title" class="w-full h-8 rounded-md border border-slate-200 px-2 text-xs font-semibold">
                          <input [(ngModel)]="item.description" placeholder="Description" class="mt-1 w-full h-8 rounded-md border border-slate-200 px-2 text-xs text-slate-500">
                        </td>
                        <td class="px-4 py-3"><input [(ngModel)]="item.quantity" type="number" min="0" step="0.01" class="w-full h-8 rounded-md border border-slate-200 px-2 text-xs text-right"></td>
                        <td class="px-4 py-3"><input [(ngModel)]="item.unit" class="w-full h-8 rounded-md border border-slate-200 px-2 text-xs"></td>
                        <td class="px-4 py-3"><input [(ngModel)]="item.unit_price" type="number" min="0" step="0.01" class="w-full h-8 rounded-md border border-slate-200 px-2 text-xs text-right"></td>
                        <td class="px-4 py-3 text-right font-semibold text-slate-900 text-sm">{{ lineTotal(item) | currency:'NZD':'symbol':'1.2-2' }}</td>
                        <td class="px-2 py-3"><button type="button" (click)="removeItem($index)" class="w-8 h-8 rounded-md text-rose-500 hover:bg-rose-50"><i class="ti ti-trash"></i></button></td>
                      </tr>
                    }
                  </tbody>
                </table>
              </div>
            </section>

            <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm p-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <textarea [(ngModel)]="form.delivery_instructions" rows="4" placeholder="Delivery instructions" class="rounded-xl border border-slate-200 px-3 py-2 text-xs"></textarea>
              <textarea [(ngModel)]="form.notes" rows="4" placeholder="Internal notes" class="rounded-xl border border-slate-200 px-3 py-2 text-xs"></textarea>
            </section>
          </div>

          <aside class="space-y-4">
            <section class="bg-white rounded-xl border border-[#E7EEF5] shadow-sm p-4 sticky top-4">
              <p class="text-sm font-bold text-slate-900">Summary</p>
              <div class="mt-4 space-y-2">
                <div class="flex items-center justify-between text-xs text-slate-500"><span>Items</span><strong class="text-slate-900">{{ form.items.length }}</strong></div>
                <div class="flex items-center justify-between text-xs text-slate-500"><span>Subtotal</span><strong class="text-slate-900">{{ subtotal() | currency:'NZD':'symbol':'1.2-2' }}</strong></div>
                <div class="flex items-center justify-between text-xs text-slate-500"><span>GST</span><strong class="text-slate-900">{{ tax() | currency:'NZD':'symbol':'1.2-2' }}</strong></div>
                <div class="flex items-center justify-between text-sm text-slate-900 py-3 border-t border-slate-200 mt-2"><span class="font-bold">Total</span><strong>{{ total() | currency:'NZD':'symbol':'1.2-2' }}</strong></div>
              </div>
              @if (form.stock_updated_at) {
                <div class="mt-3 rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-xs text-emerald-700 font-semibold">Stock received on {{ form.stock_updated_at | date:'dd MMM yyyy, h:mm a' }}</div>
              }
            </section>
          </aside>
        </div>
        }
      </div>
    </div>

    @if (showSupplierModal()) {
      <div class="fixed inset-0 z-[100] bg-slate-950/40 backdrop-blur-sm flex items-center justify-center p-4">
        <div class="bg-white rounded-xl shadow-2xl border border-slate-200 w-full max-w-3xl overflow-hidden">
          <div class="px-5 py-4 border-b border-slate-100 flex items-center justify-between">
            <div>
              <p class="text-sm font-bold text-slate-900">Edit Supplier</p>
              <p class="text-[11px] text-slate-500">Updates the saved supplier details used by purchase orders.</p>
            </div>
            <button type="button" (click)="showSupplierModal.set(false)" class="w-8 h-8 rounded-lg bg-slate-50 text-slate-500 hover:bg-slate-100"><i class="ti ti-x"></i></button>
          </div>
          <div class="p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
            <label class="text-xs font-semibold text-slate-600">Supplier name <span class="text-rose-500">*</span>
              <input [(ngModel)]="supplierForm.name" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
            </label>
            <label class="text-xs font-semibold text-slate-600">Email
              <input [(ngModel)]="supplierForm.email" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
            </label>
            <label class="text-xs font-semibold text-slate-600">Phone
              <input [(ngModel)]="supplierForm.phone" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
            </label>
            <label class="text-xs font-semibold text-slate-600">Contact person
              <input [(ngModel)]="supplierForm.contact_person" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
            </label>
            <label class="text-xs font-semibold text-slate-600">GST number
              <input [(ngModel)]="supplierForm.gst_number" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
            </label>
            <label class="text-xs font-semibold text-slate-600">Account number
              <input [(ngModel)]="supplierForm.account_number" class="mt-1 w-full h-9 rounded-lg border border-slate-200 px-3 text-xs">
            </label>
            <label class="md:col-span-3 text-xs font-semibold text-slate-600">Address
              <textarea [(ngModel)]="supplierForm.address" rows="3" class="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-xs"></textarea>
            </label>
            <label class="md:col-span-3 text-xs font-semibold text-slate-600">Notes
              <textarea [(ngModel)]="supplierForm.notes" rows="2" class="mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-xs"></textarea>
            </label>
          </div>
          <div class="px-5 py-4 border-t border-slate-100 flex justify-end gap-2">
            <button type="button" (click)="showSupplierModal.set(false)" class="h-9 px-4 rounded-lg border border-slate-200 bg-white text-slate-600 text-xs font-semibold hover:bg-slate-50">Cancel</button>
            <button type="button" (click)="saveSupplierDetails()" [disabled]="isSavingSupplier()" class="h-9 px-4 rounded-lg bg-[#0C3A5A] text-white text-xs font-bold hover:bg-[#082d46] disabled:opacity-50">Save Supplier</button>
          </div>
        </div>
      </div>
    }
  `
})
export class AdminPurchaseOrderDetailComponent implements OnInit {
  private api = inject(ApiService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  suppliers = signal<any[]>([]);
  templates = signal<any[]>([]);
  materialResults = signal<any[]>([]);
  isSaving = signal(false);
  isLoadingOrder = signal(false);
  isSavingSupplier = signal(false);
  showSupplierModal = signal(false);
  materialSearch = '';
  selectedTemplateId: number | null = null;
  statuses = ['Draft', 'Issued', 'Partially Received', 'Received', 'Cancelled'];
  form: any = this.emptyForm();
  supplierForm: any = this.emptySupplierForm();
  isExistingOrderRoute = false;

  ngOnInit() {
    this.loadSuppliers();
    this.loadTemplates();
    const id = this.route.snapshot.paramMap.get('id');
    if (id && id !== 'new') {
      this.isExistingOrderRoute = true;
      this.loadPurchaseOrder(id);
    }
    if (!id || id === 'new') this.addManualItem();
  }

  emptyForm() {
    return {
      id: null,
      po_no: '',
      supplier_id: null,
      supplier: { name: '', email: '', phone: '', contact_person: '', address: '', gst_number: '', account_number: '' },
      model_type: 'General',
      reference_id: '',
      order_date: new Date().toISOString().slice(0, 10),
      required_date: '',
      status: 'Draft',
      gst_mode: 'GST',
      notes: '',
      delivery_instructions: '',
      public_token: '',
      stock_updated_at: '',
      items: []
    };
  }

  emptySupplierForm() {
    return { id: null, name: '', email: '', phone: '', contact_person: '', address: '', gst_number: '', account_number: '', notes: '', is_active: 1 };
  }

  loadSuppliers() {
    this.api.getSuppliers().subscribe({ next: (res: any) => this.suppliers.set(res.data?.suppliers || []) });
  }

  selectedSupplier(): any {
    const supplier = this.suppliers().find(item => Number(item.id) === Number(this.form.supplier_id));
    if (supplier) return supplier;
    return this.form.supplier_id ? { ...this.form.supplier, id: this.form.supplier_id } : null;
  }

  openSupplierModal(supplier: any) {
    this.supplierForm = { ...this.emptySupplierForm(), ...supplier, is_active: supplier?.is_active ?? 1 };
    this.showSupplierModal.set(true);
  }

  saveSupplierDetails() {
    if (!this.supplierForm.name?.trim()) return showToast('error', 'Supplier name is required.');
    this.isSavingSupplier.set(true);
    this.api.saveSupplier(this.supplierForm).subscribe({
      next: () => {
        this.isSavingSupplier.set(false);
        this.showSupplierModal.set(false);
        if (Number(this.supplierForm.id) === Number(this.form.supplier_id)) {
          this.form.supplier = { ...this.form.supplier, ...this.supplierForm };
        }
        showToast('success', 'Supplier details updated.');
        this.loadSuppliers();
      },
      error: (err) => {
        this.isSavingSupplier.set(false);
        showToast('error', err.error?.message || 'Could not update supplier.');
      }
    });
  }

  loadTemplates() {
    this.api.getPurchaseOrderTemplates().subscribe({ next: (res: any) => this.templates.set(res.data?.templates || []) });
  }

  loadPurchaseOrder(id: string) {
    this.isLoadingOrder.set(true);
    this.api.getPurchaseOrder(id).subscribe({
      next: (res: any) => {
        const po = res.data?.purchase_order;
        this.form = {
          ...this.emptyForm(),
          ...po,
          supplier_id: Number(po.supplier_id),
          supplier: {
            ...this.emptySupplierForm(),
            id: Number(po.supplier_id),
            name: po.supplier_name || '',
            email: po.supplier_email || '',
            phone: po.supplier_phone || '',
            contact_person: po.contact_person || '',
            address: po.supplier_address || '',
            gst_number: po.gst_number || '',
            account_number: po.account_number || ''
          },
          items: (po.items || []).map((item: any) => ({ ...item, quantity: Number(item.quantity), unit_price: Number(item.unit_price) }))
        };
        this.isLoadingOrder.set(false);
      },
      error: () => {
        this.isLoadingOrder.set(false);
        showToast('error', 'Could not load purchase order.');
      }
    });
  }

  searchMaterials() {
    if (this.materialSearch.trim().length < 2) {
      this.materialResults.set([]);
      return;
    }
    this.api.getMaterials({ search: this.materialSearch, include_children: false, limit: 12 }).subscribe({
      next: (res: any) => this.materialResults.set(res.data?.materials || [])
    });
  }

  addMaterialItem(mat: any) {
    this.form.items.push({
      material_id: mat.id,
      sku: mat.serial_no,
      title: mat.name,
      description: mat.description || '',
      quantity: 1,
      unit: mat.unit || 'EA',
      unit_price: Number(mat.cost_price || 0)
    });
    this.materialSearch = '';
    this.materialResults.set([]);
  }

  addManualItem() {
    this.form.items.push({ material_id: null, sku: '', title: '', description: '', quantity: 1, unit: 'EA', unit_price: 0 });
  }

  applyTemplate() {
    if (!this.selectedTemplateId) return;
    this.api.getPurchaseOrderTemplate(this.selectedTemplateId).subscribe({
      next: (res: any) => {
        const items = (res.data?.template?.items || []).map((item: any) => ({
          material_id: item.material_id ? Number(item.material_id) : null,
          sku: item.sku || '',
          title: item.title || '',
          description: item.description || '',
          quantity: Number(item.quantity || 1),
          unit: item.unit || 'EA',
          unit_price: Number(item.unit_price || 0)
        }));
        if (!items.length) return showToast('error', 'Template has no items.');
        this.form.items = items;
        showToast('success', 'Template applied.');
      },
      error: () => showToast('error', 'Could not load template.')
    });
  }

  removeItem(index: number) {
    this.form.items.splice(index, 1);
  }

  lineTotal(item: any): number {
    return Number(item.quantity || 0) * Number(item.unit_price || 0);
  }

  subtotal(): number {
    return this.form.items.reduce((sum: number, item: any) => sum + this.lineTotal(item), 0);
  }

  tax(): number {
    return this.form.gst_mode === 'GST' ? this.subtotal() * 0.15 : 0;
  }

  total(): number {
    return this.subtotal() + this.tax();
  }

  save() {
    if (!this.form.supplier_id && !this.form.supplier.name?.trim()) return showToast('error', 'Supplier is required.');
    if (!this.form.items.length) return showToast('error', 'Add at least one purchase item.');
    this.isSaving.set(true);
    const payload = { ...this.form };
    if (payload.model_type === 'General') payload.reference_id = null;
    this.api.savePurchaseOrder(payload).subscribe({
      next: (res: any) => {
        showToast('success', 'Purchase order saved.');
        this.isSaving.set(false);
        if (!this.form.id) this.router.navigate(['/admin/financials/purchase-orders', res.data.id]);
        this.loadSuppliers();
        this.loadPurchaseOrder(res.data.id);
      },
      error: (err) => {
        this.isSaving.set(false);
        showToast('error', err.error?.message || 'Could not save purchase order.');
      }
    });
  }

  openPdf() {
    if (!this.form.id) return;
    this.router.navigate(['/admin/financials/pdf', 'purchase-order', this.form.id]);
  }

  sendToSupplier() {
    if (!this.form.id) return;
    this.api.sendPurchaseOrder(this.form.id).subscribe({
      next: (res: any) => {
        showToast('success', 'Purchase order emailed to supplier.');
        this.loadPurchaseOrder(this.form.id);
      },
      error: (err) => showToast('error', err.error?.message || 'Could not send purchase order.')
    });
  }

  updateStock() {
    if (!this.form.id || this.form.stock_updated_at) return;
    if (!confirm('Update stock quantities from this purchase order? This can only be done once.')) return;
    this.api.updatePurchaseOrderStock(this.form.id).subscribe({
      next: () => {
        showToast('success', 'Stock updated from purchase order.');
        this.loadPurchaseOrder(this.form.id);
      },
      error: (err) => showToast('error', err.error?.message || 'Could not update stock.')
    });
  }

  statusBadgeClass(status: string): string {
    if (status === 'Received') return 'text-emerald-700 bg-emerald-50 border-emerald-100';
    if (status === 'Issued' || status === 'Partially Received') return 'text-sky-700 bg-sky-50 border-sky-100';
    if (status === 'Cancelled') return 'text-rose-700 bg-rose-50 border-rose-100';
    return 'text-slate-600 bg-slate-50 border-slate-200';
  }
}
